import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mangaList, type Manga } from '@/mocks/manga';
import { getArticleByChapter, type Article } from '@/mocks/articles';

export default function ArticlePage() {
  const { mangaId: mangaIdStr, chapterId: chapterIdStr } = useParams<{
    mangaId: string;
    chapterId: string;
  }>();
  const navigate = useNavigate();
  const mangaId = Number(mangaIdStr);
  const chapterNumber = Number(chapterIdStr);

  const [article, setArticle] = useState<Article | undefined>(undefined);
  const [manga, setManga] = useState<Manga | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    // Simulate loading
    const timer = setTimeout(() => {
      const foundArticle = getArticleByChapter(mangaId, chapterNumber);
      const foundManga = mangaList.find((m) => m.id === mangaId);
      setArticle(foundArticle);
      setManga(foundManga);
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [mangaId, chapterNumber]);

  const changeChapter = useCallback(
    (newChapter: number) => {
      window.scrollTo({ top: 0, behavior: 'instant' });
      navigate(`/article/${mangaId}/${newChapter}`);
    },
    [mangaId, navigate],
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-foreground-500">加载中...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!article || !manga) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="font-heading text-2xl text-foreground-900 mb-4">章节不存在</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryLabel =
    article.category === 'qihuan'
      ? '奇幻'
      : article.category === 'rexue'
        ? '热血'
        : article.category === 'lianai'
          ? '恋爱'
          : article.category === 'xuanyi'
            ? '悬疑'
            : article.category === 'kongbu'
              ? '恐怖'
              : article.category;

  return (
    <>
      <ScrollToTop />

      {/* Schema.org Article JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: `${manga.title} - ${article.title}`,
          description: `在线免费阅读${manga.title}${article.title}，下拉式漫画阅读体验，尽在073漫画网。`,
          author: {
            '@type': 'Person',
            name: manga.author,
          },
          datePublished: article.publishedAt,
          publisher: {
            '@type': 'Organization',
            name: '073漫画网',
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `https://073mh.com/article/${mangaId}/${chapterNumber}`,
          },
        })}
      </script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            {/* Breadcrumb */}
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${categoryLabel}漫画`, href: `/category/${manga.category}` },
                { label: manga.title },
                { label: article.title },
              ]}
            />

            {/* Article Header */}
            <article>
              <header className="text-center mb-8">
                <h1 className="font-heading text-xl md:text-2xl text-foreground-900 mb-2">
                  {manga.title}
                </h1>
                <h2 className="text-base text-foreground-600">{article.title}</h2>
                <div className="flex items-center justify-center gap-4 mt-3 text-xs text-foreground-400">
                  <span>作者：{manga.author}</span>
                  <span>更新：{article.publishedAt}</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {(article.views / 10000).toFixed(1)}万阅读
                  </span>
                </div>
              </header>

              {/* Chapter Content - 下拉式阅读 */}
              <div className="bg-background-100 rounded-xl p-6 md:p-10 mb-8 leading-loose text-foreground-800">
                {article.content.map((paragraph, index) => (
                  <p key={index} className="mb-4 text-base">
                    {paragraph}
                  </p>
                ))}

                {/* Pagination hint */}
                <div className="text-center mt-8 pt-6 border-t border-background-200">
                  <p className="text-sm text-foreground-400 flex items-center justify-center gap-2">
                    <i className="ri-arrow-down-line animate-bounce"></i>
                    下拉继续阅读
                    <i className="ri-arrow-down-line animate-bounce"></i>
                  </p>
                </div>
              </div>

              {/* Chapter Navigation */}
              <nav className="flex items-center justify-between gap-4 pb-10" aria-label="章节导航">
                {article.prevChapter ? (
                  <button
                    onClick={() => changeChapter(article.prevChapter!)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-500 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    上一话
                  </button>
                ) : (
                  <div />
                )}

                <Link
                  to={`/category/${manga.category}`}
                  className="px-5 py-2.5 rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors text-sm font-medium whitespace-nowrap"
                >
                  返回分类
                </Link>

                {article.nextChapter ? (
                  <button
                    onClick={() => changeChapter(article.nextChapter!)}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors text-sm font-medium whitespace-nowrap cursor-pointer"
                  >
                    下一话
                    <i className="ri-arrow-right-line"></i>
                  </button>
                ) : (
                  <div />
                )}
              </nav>
            </article>

            {/* Related Manga */}
            <section className="pb-14">
              <h3 className="font-heading text-lg text-foreground-900 mb-4">相关推荐</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-product-shop="">
                {mangaList
                  .filter((m) => m.category === manga.category && m.id !== manga.id)
                  .slice(0, 4)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/article/${related.id}/1`}
                      className="group block rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-[3/4] overflow-hidden">
                        <img
                          src={related.cover}
                          alt={related.title}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          width="300"
                          height="400"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="font-heading text-sm text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {related.title}
                        </h4>
                        <p className="text-xs text-foreground-500 mt-1">{related.author}</p>
                      </div>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}